//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { ConfirmationModalActions } from '@account-settings/redux/facebook-instagram/confirmation-modal/confirmationModalActions';
import ConfirmationModalReducer from '@account-settings/redux/facebook-instagram/confirmation-modal/confirmationModalReducer';
import { FacebookSettingsModalActions } from '@account-settings/redux/facebook-instagram/facebook-settings-modal/facebookSettingsModalActions';
import FacebookSettingsModalReducer from '@account-settings/redux/facebook-instagram/facebook-settings-modal/facebookSettingsModalReducer';
import { FacebookInstagramSettingsActions } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsActions';
import { FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsConstants';
import {
  ICombinedFacebookInstagramSettingsState,
  IFacebookInstagramSettingsState,
} from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsModel';
import { InstagramSettingsModalActions } from '@account-settings/redux/facebook-instagram/instagram-settings-modal/instagramSettingsModalActions';
import InstagramSettingsModalReducer from '@account-settings/redux/facebook-instagram/instagram-settings-modal/instagramSettingsModalReducer';

const initialState: IFacebookInstagramSettingsState = {
  connectedFacebookPageToken: undefined,
  connectedInstagramAccount: undefined,
  facebookUserToken: undefined,
  fetchConnectedInstagramAccountError: undefined,
  fetchConnectedFacebookPageTokenError: undefined,
  flags: {
    isHandlingOAuth: false,
    isHandlingDeauth: false,
    isFetchingFacebookUserToken: false,
    isFetchingConnectedFacebookPageToken: false,
    isFetchingConnectedInstagramAccount: false,
  },
};

function FacebookInstagramSettingsReducer(
  state: IFacebookInstagramSettingsState = initialState,
  action: FacebookInstagramSettingsActions,
): IFacebookInstagramSettingsState {
  switch (action.type) {
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.START_OAUTH:
      return {
        ...state,
        flags: {
          ...state.flags,
          isHandlingOAuth: true,
        },
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_DEAUTH_FB:
      return {
        ...state,
        flags: {
          ...state.flags,
          isHandlingDeauth: true,
        },
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_DEAUTH_FB_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isHandlingDeauth: false,
        },
        connectedFacebookPageToken: initialState.connectedFacebookPageToken,
        connectedInstagramAccount: initialState.connectedInstagramAccount,
        facebookUserToken: initialState.facebookUserToken,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_DEAUTH_FB_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isHandlingDeauth: false,
        },
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_FACEBOOK_USER_TOKEN:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingFacebookUserToken: true,
        },
        facebookUserToken: initialState.facebookUserToken,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_FACEBOOK_USER_TOKEN_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingFacebookUserToken: false,
        },
        facebookUserToken: action.facebookUserToken,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_FACEBOOK_USER_TOKEN_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingFacebookUserToken: false,
        },
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_IG_ACCOUNT:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: true,
        },
        connectedInstagramAccount: initialState.connectedInstagramAccount,
        fetchConnectedInstagramAccountError:
          initialState.fetchConnectedInstagramAccountError,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: false,
        },
        connectedInstagramAccount: action.connectedInstagramAccount,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: false,
        },
        fetchConnectedInstagramAccountError: action.message,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_IG_ACCOUNT_MULTIHANDLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: true,
        },
        connectedInstagramAccounts: initialState.connectedInstagramAccounts,
        fetchConnectedInstagramAccountError:
          initialState.fetchConnectedInstagramAccountError,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_SUCCEEDED_MULTIHANDLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: false,
        },
        connectedInstagramAccounts: action.connectedInstagramAccounts,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_FAILED_MULTIHANDLE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedInstagramAccount: false,
        },
        fetchConnectedInstagramAccountError: action.message,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_FB_PAGE:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedFacebookPageToken: true,
        },
        connectedFacebookPageToken: initialState.connectedFacebookPageToken,
        fetchConnectedFacebookPageTokenError:
          initialState.fetchConnectedFacebookPageTokenError,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_FB_PAGE_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedFacebookPageToken: false,
        },
        connectedFacebookPageToken: action.connectedFacebookPageToken,
      };
    case FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_FB_PAGE_FAILED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingConnectedFacebookPageToken: false,
        },
        fetchConnectedFacebookPageTokenError: action.message,
      };
    default:
      return state;
  }
}

export default function CombinedFacebookInstagramSettingsReducer(
  state: ICombinedFacebookInstagramSettingsState | undefined,
  action:
    | FacebookInstagramSettingsActions
    | FacebookSettingsModalActions
    | InstagramSettingsModalActions
    | ConfirmationModalActions,
): ICombinedFacebookInstagramSettingsState {
  return {
    ...FacebookInstagramSettingsReducer(
      state,
      action as FacebookInstagramSettingsActions,
    ),
    facebookModal: FacebookSettingsModalReducer(
      state?.facebookModal,
      action as FacebookSettingsModalActions,
    ),
    instagramModal: InstagramSettingsModalReducer(
      state?.instagramModal,
      action as InstagramSettingsModalActions,
    ),
    confirmationModal: ConfirmationModalReducer(
      state?.confirmationModal,
      action as ConfirmationModalActions,
    ),
  };
}
